import React, { useEffect, useRef, useState } from 'react';
import { useStore } from '../../store';
import { Controller } from 'react-hook-form';
import { AddressSuggestionModal } from '../../pages/CreatePatientForm/AddressSuggestionModal';
import { FeatureFlag } from '../../constants/featureFlags';
import { client } from '../../services/api';
import { initializeEmrSelectionEndpointIntakeIntakeIdInitializeEmrSelectionPost } from '../../services/api/sdk.gen';
import {
  Alert,
  AlertDescription,
  Card,
  CardHeader,
  CardContent,
  Checkbox,
} from '@roger/ui-components';
import {
  AlertTriangle,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
} from 'lucide-react';
import { useCreateEpisodeStepForm } from '../../hooks/useCreatePatientForm/useCreateEpisodeStepForm';
import { useIntake } from '../../hooks/useIntake';
import { useToast } from '../../hooks/useToastContext';
import { PatientCreateButton } from './PatientCreateButton';
import MarkCompletedButton from './MarkCompletedButton';
import { KinnserCreateEpisodeStepFormContent } from '../../pages/CreatePatientForm/KinnserCreateEpisodeStepFormContent';
import { RogerCreateEpisodeStepFormContent } from '../../pages/CreatePatientForm/RogerCreateEpisodeStepFormContent';
import { PatientMatchModal } from '../../pages/CreatePatientForm/PatientMatchModal';
import type {
  KinnserCreatePatientResponse,
  RogerOnboardPatientResponse,
} from '../../services/api/types.gen';
import type { KinnserOnboardPatientResponse } from '../../hooks/useCreatePatientForm/useKinnserOnboarding';

interface CreateEpisodeStepProps {
  onComplete?: () => Promise<void>;
  onStatusChange?: () => void | Promise<void>;
}

const CreateEpisodeStep: React.FC<CreateEpisodeStepProps> = ({
  onComplete,
  onStatusChange,
}) => {
  const hasFeatureFlag = useStore(state => state.hasFeatureFlag);
  const { confirmTabStatus } = useIntake();
  const { error: showError } = useToast();

  const {
    // Form
    control,
    register,
    errors,
    setValue,
    handleFieldBlur,

    // Watched values
    hhCahpsOptOut,
    kinnserLanguageKey,
    pushToRoger,
    pushToKinnser,

    // Save status
    saveStatus,
    saveToServer,

    // Clinics
    clinics,
    clinicData,
    loadingClinics,
    loadingClinicData,

    // Form validation
    isValid,

    // Create Episode Generic
    createResult,
    creatingInEMR,
    isUsingOnboarding,

    // Kinnser ceation/onboarding
    handleCreateInKinnser,

    // Roger creation
    handleCreateInRoger,

    // Intake data
    selectedIntake,

    // Patient matching
    patientMatches,
    patientMatchModalOpen,
    setPatientMatchModalOpen,
    handleNameFieldBlur,
    isMatchingPatients,

    // Address validation
    addressSuggestion,
    addressModalOpen,
    isValidatingAddress,
    setAddressModalOpen,
    handleAddressFieldBlur,
    handleAcceptAddressSuggestion,

    // Clinic field handler
    handleClinicFieldBlur,
  } = useCreateEpisodeStepForm();

  // Track if we've already initialized EMR selection for this intake
  const emrSelectionInitialized = useRef<string | null>(null);
  const [initializingEmrSelection, setInitializingEmrSelection] =
    useState(false);
  // Prevents duplicate confirmTabStatus calls when this effect re-runs before
  // onStatusChange() has refetched and updated tab_statuses to 'complete'.
  const hasAutoConfirmedPushToEmrRef = useRef(false);

  // Reset auto-confirm tracking when switching to a different intake
  useEffect(() => {
    hasAutoConfirmedPushToEmrRef.current = false;
  }, [selectedIntake?.id]);

  // Auto-mark Push to EMR tab complete when onboarding/create succeeds
  useEffect(() => {
    if (
      !selectedIntake?.id ||
      !createResult ||
      hasAutoConfirmedPushToEmrRef.current
    )
      return;

    const isRogerSuccess =
      pushToRoger &&
      (createResult as RogerOnboardPatientResponse)?.success === true;
    const isKinnserSuccess =
      pushToKinnser &&
      (isUsingOnboarding
        ? (createResult as KinnserOnboardPatientResponse)?.patient_creation
            ?.success === true
        : (createResult as KinnserCreatePatientResponse)?.success === true);
    const isSuccess = isRogerSuccess || isKinnserSuccess;
    const tabComplete =
      selectedIntake.tab_statuses?.push_to_emr === 'complete' ||
      selectedIntake.tab_statuses?.push_to_emr === 'complete_with_errors';
    if (!isSuccess || tabComplete) return;

    hasAutoConfirmedPushToEmrRef.current = true;
    confirmTabStatus(selectedIntake.id, 'push_to_emr', 'complete')
      .then(() => {
        onStatusChange?.();
      })
      .catch(() => {
        showError(
          'Failed to mark tab complete',
          'The Push to EMR tab could not be marked complete.'
        );
      });
  }, [
    selectedIntake?.id,
    selectedIntake?.tab_statuses?.push_to_emr,
    createResult,
    pushToRoger,
    pushToKinnser,
    isUsingOnboarding,
    confirmTabStatus,
    onStatusChange,
    showError,
  ]);

  // Initialize EMR selection when entering the Push to EMR step
  // This auto-selects the EMR option if only one is enabled
  useEffect(() => {
    const initializeEmrSelection = async () => {
      if (!selectedIntake?.id) return;

      // Only initialize once per intake
      if (emrSelectionInitialized.current === selectedIntake.id) return;
      emrSelectionInitialized.current = selectedIntake.id;

      setInitializingEmrSelection(true);
      try {
        const response =
          await initializeEmrSelectionEndpointIntakeIntakeIdInitializeEmrSelectionPost(
            {
              path: { intake_id: selectedIntake.id },
              client,
            }
          );

        // Update form values if the backend auto-set the flags
        if (response.data) {
          if (
            response.data.push_to_roger !== selectedIntake.push_to_roger ||
            response.data.push_to_kinnser !== selectedIntake.push_to_kinnser
          ) {
            setValue('push_to_roger', response.data.push_to_roger ?? false);
            setValue('push_to_kinnser', response.data.push_to_kinnser ?? false);
          }
        }
      } catch (error) {
        // Silently fail if the API call fails
        console.error('Failed to initialize EMR selection:', error);
      } finally {
        setInitializingEmrSelection(false);
      }
    };

    initializeEmrSelection();
  }, [
    selectedIntake?.id,
    selectedIntake?.push_to_roger,
    selectedIntake?.push_to_kinnser,
    setValue,
  ]);

  // Check if user has permission to create patients
  if (!hasFeatureFlag(FeatureFlag.INTAKE_SCHEDULING_SUPPORT)) {
    return (
      <div className='p-6'>
        <Alert className='border-red-200 bg-red-50'>
          <AlertDescription className='text-red-800 flex gap-2 items-center'>
            <AlertTriangle className='h-4 w-4 text-red-600' />
            You don't have permission to create patients. Please contact your
            administrator if you need access.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!selectedIntake) {
    return (
      <div className='h-screen flex items-center justify-center'>
        <Loader2 className='h-8 w-8 animate-spin text-gray-400' />
      </div>
    );
  }

  const isSocCreated: boolean = !!(
    selectedIntake?.roger_create_episode ||
    selectedIntake?.kinnser_create_episode
  );

  const showRogerOption = hasFeatureFlag(FeatureFlag.INTAKE_ROGER_SCHEDULING);
  const showKinnserOption = hasFeatureFlag(
    FeatureFlag.INTAKE_WELLSKY_SCHEDULING
  );

  const isLoading =
    loadingClinics || loadingClinicData || initializingEmrSelection;

  return (
    <>
      <div className='h-full flex flex-col'>
        <Card className='h-full flex flex-col'>
          <CardHeader className='border-b py-3 px-4'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-3'>
                {saveStatus === 'saving' && (
                  <div className='flex items-center gap-2 text-xs text-gray-500'>
                    <Loader2 className='h-3 w-3 animate-spin' />
                    <span>Saving...</span>
                  </div>
                )}
                {saveStatus === 'saved' && (
                  <div className='text-xs text-green-600'>Saved</div>
                )}
                {saveStatus === 'error' && (
                  <div className='text-xs text-red-600'>Error saving</div>
                )}
                {isSocCreated && showRogerOption && showKinnserOption && (
                  <Alert className='py-2 px-3 bg-blue-50 border-blue-200'>
                    <AlertDescription className='text-blue-800 text-xs flex gap-2 items-center'>
                      <AlertCircle className='h-4 w-4 flex-shrink-0' />
                      <span>
                        Patient will be created in the EMR where the SOC was
                        scheduled. To change EMR, go to the Scheduling step.
                      </span>
                    </AlertDescription>
                  </Alert>
                )}
              </div>
              <div className='ml-4 flex min-w-0 flex-col items-stretch gap-2 basis-[15rem] shrink-0 grow-0'>
                <div className='w-full min-w-0'>
                  <PatientCreateButton
                    handleCreate={
                      pushToRoger
                        ? handleCreateInRoger
                        : pushToKinnser
                          ? handleCreateInKinnser
                          : async () => {}
                    }
                    onComplete={onComplete}
                    creating={creatingInEMR}
                    saveStatus={saveStatus}
                    loadingClinics={loadingClinics}
                    loadingClinicData={loadingClinicData}
                    selectedIntake={selectedIntake}
                    isUsingOnboarding={isUsingOnboarding}
                    pushToEmrTabComplete={
                      selectedIntake.tab_statuses?.push_to_emr === 'complete' ||
                      selectedIntake.tab_statuses?.push_to_emr ===
                        'complete_with_errors'
                    }
                    createResult={createResult}
                    isValid={isValid}
                    pushToRoger={pushToRoger}
                    pushToKinnser={pushToKinnser}
                    className='w-full min-w-0'
                  />
                </div>
                {selectedIntake && (
                  <div className='w-full min-w-0'>
                    <MarkCompletedButton
                      intakeId={selectedIntake.id}
                      tabKey='push_to_emr'
                      isComplete={
                        selectedIntake.tab_statuses?.push_to_emr ===
                          'complete' ||
                        selectedIntake.tab_statuses?.push_to_emr ===
                          'complete_with_errors'
                      }
                      onStatusChange={onStatusChange}
                      disabled={creatingInEMR}
                      className='w-full min-w-0'
                    />
                  </div>
                )}
              </div>
            </div>

            {showRogerOption && showKinnserOption && (
              <div className='flex items-center gap-6 mt-3'>
                <div className='flex items-center gap-2'>
                  <Controller
                    name='push_to_roger'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <Checkbox
                        id='pushToRoger'
                        checked={!!value}
                        disabled={isSocCreated}
                        onCheckedChange={async checked => {
                          const isChecked = checked === true;
                          onChange(isChecked);

                          // If checking Roger, uncheck Kinnser
                          if (isChecked) {
                            setValue('push_to_kinnser', false);
                            await saveToServer({
                              push_to_roger: true,
                              push_to_kinnser: false,
                            });
                          } else {
                            await saveToServer({
                              push_to_roger: false,
                            });
                          }
                        }}
                      />
                    )}
                  />
                  <label
                    htmlFor='pushToRoger'
                    className={`text-sm font-medium cursor-pointer ${
                      isSocCreated ? 'text-gray-400 cursor-not-allowed' : ''
                    }`}
                  >
                    Roger EMR
                  </label>
                </div>

                <div className='flex items-center gap-2'>
                  <Controller
                    name='push_to_kinnser'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <Checkbox
                        id='pushToKinnser'
                        checked={!!value}
                        disabled={isSocCreated}
                        onCheckedChange={async checked => {
                          const isChecked = checked === true;
                          onChange(isChecked);

                          // If checking Kinnser, uncheck Roger
                          if (isChecked) {
                            setValue('push_to_roger', false);
                            await saveToServer({
                              push_to_kinnser: true,
                              push_to_roger: false,
                            });
                          } else {
                            await saveToServer({
                              push_to_kinnser: false,
                            });
                          }
                        }}
                      />
                    )}
                  />
                  <label
                    htmlFor='pushToKinnser'
                    className={`text-sm font-medium cursor-pointer ${
                      isSocCreated ? 'text-gray-400 cursor-not-allowed' : ''
                    }`}
                  >
                    Kinnser EMR
                  </label>
                </div>
              </div>
            )}

            {/* Validation errors - hide while loading clinic data */}
            {!isLoading && Object.keys(errors).length > 0 && (
              <Alert className='mt-3 py-2 px-3 bg-red-50 border-red-100 text-red-900 text-xs'>
                <div className='flex items-start gap-2'>
                  <AlertCircle className='h-4 w-4 mt-0.5 flex-shrink-0 text-red-600' />
                  <div className='flex-1'>
                    <p className='font-medium mb-1'>
                      Invalid fields required to push to EMR:
                    </p>
                    <ul
                      className='flex flex-wrap gap-1'
                      role='list'
                      aria-label='Validation errors'
                    >
                      {Object.entries(errors)
                        .map(([field, error]) => {
                          // Clean up error message to remove redundant " is required" suffix
                          // since the header already states they are required fields
                          const message =
                            (error as { message?: string })?.message || '';
                          const cleanMessage = message.replace(
                            / is required$/,
                            ''
                          );

                          return { field, cleanMessage };
                        })
                        .filter(({ cleanMessage }) => cleanMessage !== '')
                        .map(({ field, cleanMessage }, i, arr) => (
                          <li key={field} role='listitem' className='text-xs'>
                            {cleanMessage}
                            {i < arr.length - 1 ? ',' : ''}
                          </li>
                        ))}
                    </ul>
                  </div>
                </div>
              </Alert>
            )}
            {/* Validation errors or success message */}
            {createResult && (
              <div className='mt-3'>
                {pushToRoger ? (
                  (createResult as RogerOnboardPatientResponse).success ? (
                    <div className='p-3 bg-green-50 border border-green-200 rounded-md'>
                      <div className='flex items-center gap-2 text-green-800'>
                        <CheckCircle className='h-4 w-4' />
                        <span className='font-medium'>
                          {isUsingOnboarding
                            ? 'Patient, episode, task, and attachment created in Roger'
                            : 'Patient created successfully in Roger'}
                        </span>
                      </div>
                      {(createResult as RogerOnboardPatientResponse)
                        .message && (
                        <div className='text-sm text-green-700 mt-2'>
                          {
                            (createResult as RogerOnboardPatientResponse)
                              .message
                          }
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className='p-3 bg-red-50 border border-red-200 rounded-md'>
                      <div className='flex items-center gap-2 text-red-800 mb-2'>
                        <XCircle className='h-4 w-4' />
                        <span className='font-medium'>
                          {isUsingOnboarding
                            ? 'Onboarding Failed'
                            : 'Validation Errors'}
                        </span>
                      </div>
                      <div className='text-sm text-red-700'>
                        {(createResult as RogerOnboardPatientResponse).message}
                      </div>
                    </div>
                  )
                ) : isUsingOnboarding ? (
                  // Onboarding result display
                  (createResult as KinnserOnboardPatientResponse)
                    .patient_creation.success ? (
                    <div className='p-3 bg-green-50 border border-green-200 rounded-md'>
                      <div className='flex items-center gap-2 text-green-800'>
                        <CheckCircle className='h-4 w-4' />
                        <span className='font-medium'>
                          Patient, episode, task, and attachment created in
                          Kinnser
                        </span>
                      </div>
                      <div className='text-sm text-green-700 mt-2 space-y-1'>
                        {(createResult as KinnserOnboardPatientResponse)
                          .task_creation.patient_task_key && (
                          <div>
                            Task:{' '}
                            {
                              (createResult as KinnserOnboardPatientResponse)
                                .task_creation.task_name
                            }{' '}
                            (
                            {
                              (createResult as KinnserOnboardPatientResponse)
                                .task_creation.patient_task_key
                            }
                            )
                          </div>
                        )}
                        {(createResult as KinnserOnboardPatientResponse).upload
                          .attachments && (
                          <div>
                            Uploaded:{' '}
                            {(
                              createResult as KinnserOnboardPatientResponse
                            ).upload.attachments
                              .map(
                                att =>
                                  att.client_file ||
                                  att.server_file ||
                                  'Unknown'
                              )
                              .filter(Boolean)
                              .join(', ')}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className='p-3 bg-red-50 border border-red-200 rounded-md'>
                      <div className='flex items-center gap-2 text-red-800 mb-2'>
                        <XCircle className='h-4 w-4' />
                        <span className='font-medium'>Onboarding Failed</span>
                      </div>
                      <div className='text-sm text-red-700'>
                        {
                          (createResult as KinnserOnboardPatientResponse)
                            .patient_creation.message
                        }
                      </div>
                    </div>
                  )
                ) : // Creation result display
                (createResult as KinnserCreatePatientResponse).success ? (
                  <div className='p-3 bg-green-50 border border-green-200 rounded-md'>
                    <div className='flex items-center gap-2 text-green-800'>
                      <CheckCircle className='h-4 w-4' />
                      <span className='font-medium'>
                        Patient created successfully in Kinnser
                      </span>
                    </div>
                    {(createResult as KinnserCreatePatientResponse)
                      .kinnser_patient_id && (
                      <div className='text-sm text-green-700 mt-1'>
                        Kinnser Patient ID:{' '}
                        {
                          (createResult as KinnserCreatePatientResponse)
                            .kinnser_patient_id
                        }
                      </div>
                    )}
                  </div>
                ) : (
                  <div className='p-3 bg-red-50 border border-red-200 rounded-md'>
                    <div className='flex items-center gap-2 text-red-800 mb-2'>
                      <XCircle className='h-4 w-4' />
                      <span className='font-medium'>Validation Errors</span>
                    </div>
                    <ul className='list-disc list-inside text-sm text-red-700 space-y-1'>
                      {(
                        createResult as KinnserCreatePatientResponse
                      ).errors?.map((error, index) => (
                        <li key={index}>{error.message}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </CardHeader>
          <CardContent className='flex-1 overflow-y-auto p-4 pt-3'>
            {isLoading ? (
              <div className='h-full flex items-center justify-center'>
                <div className='flex flex-col items-center gap-4'>
                  <Loader2 className='h-8 w-8 animate-spin text-gray-400' />
                </div>
              </div>
            ) : pushToRoger ? (
              <RogerCreateEpisodeStepFormContent
                control={control}
                register={register}
                errors={errors}
                setValue={setValue}
                handleFieldBlur={handleFieldBlur}
                saveToServer={saveToServer}
                displayValues={{
                  hhCahpsOptOut: hhCahpsOptOut || false,
                }}
                clinics={clinics}
                clinicData={clinicData}
                loadingClinics={loadingClinics}
                loadingClinicData={loadingClinicData}
                selectedIntake={selectedIntake}
                patientMatches={patientMatches}
                setPatientMatchModalOpen={setPatientMatchModalOpen}
                handleNameFieldBlur={handleNameFieldBlur}
                isMatchingPatients={isMatchingPatients}
                isValidatingAddress={isValidatingAddress}
                handleAddressFieldBlur={handleAddressFieldBlur}
                kinnserLanguageKey={kinnserLanguageKey || undefined}
              />
            ) : pushToKinnser ? (
              <KinnserCreateEpisodeStepFormContent
                control={control}
                register={register}
                errors={errors}
                setValue={setValue}
                handleFieldBlur={handleFieldBlur}
                displayValues={{
                  hhCahpsOptOut: hhCahpsOptOut || false,
                  kinnserLanguageKey: kinnserLanguageKey || undefined,
                }}
                saveStatus={saveStatus}
                saveToServer={saveToServer}
                clinics={clinics}
                clinicData={clinicData}
                loadingClinics={loadingClinics}
                loadingClinicData={loadingClinicData}
                selectedIntake={selectedIntake}
                patientMatches={patientMatches}
                patientMatchModalOpen={patientMatchModalOpen}
                setPatientMatchModalOpen={setPatientMatchModalOpen}
                handleNameFieldBlur={handleNameFieldBlur}
                isMatchingPatients={isMatchingPatients}
                isValidatingAddress={isValidatingAddress}
                handleAddressFieldBlur={handleAddressFieldBlur}
                kinnserLanguageKey={kinnserLanguageKey}
                isFormValid={isValid}
                addressSuggestion={addressSuggestion}
                addressModalOpen={addressModalOpen}
                setAddressModalOpen={setAddressModalOpen}
                handleAcceptAddressSuggestion={handleAcceptAddressSuggestion}
                handleClinicFieldBlur={handleClinicFieldBlur}
              />
            ) : (
              <div className='h-full flex justify-center'>
                <div className='text-center max-w-md flex flex-col items-center'>
                  <AlertTriangle className='h-12 w-12 text-yellow-500 mb-4' />
                  <h3 className='text-lg font-semibold text-gray-900 mb-2'>
                    No Selection Made
                  </h3>
                  <p className='text-gray-500 mb-6'>
                    Toggle one of the checkboxes above to continue
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <PatientMatchModal
        patientMatchModalOpen={patientMatchModalOpen}
        setPatientMatchModalOpen={setPatientMatchModalOpen}
        patientMatches={patientMatches}
      />

      <AddressSuggestionModal
        addressModalOpen={addressModalOpen}
        setAddressModalOpen={setAddressModalOpen}
        addressSuggestion={addressSuggestion}
        handleAcceptAddressSuggestion={handleAcceptAddressSuggestion}
      />
    </>
  );
};

export default CreateEpisodeStep;
